// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-control/OperationalSignals.jsx

import React from "react";
import { Button } from "../../design-system/components";
import MissionControlSection from "./MissionControlSection";
import MissionControlStatus from "./MissionControlStatus";

function plural(value, singular, pluralValue = `${singular}s`) {
  return Number(value) === 1 ? singular : pluralValue;
}

function metric(label, value, tone = "neutral") {
  return { label, value, tone };
}

export function validationSignal(validation = {}) {
  const hasResults = Number(validation.totalResults || 0) > 0;
  const confidence =
    validation.confidenceScore !== null &&
    validation.confidenceScore !== undefined
      ? `${Number(validation.confidenceScore).toFixed(0)}%`
      : "—";

  return {
    key: "validation",
    title: "Validation Intelligence",
    description: "Validation health, evidence coverage, and confidence.",
    loaded: validation.loaded,
    statusLabel: !validation.loaded
      ? "Unavailable"
      : !hasResults ? "Not assessed" : validation.healthLabel,
    statusTone: validation.loaded && hasResults ? validation.tone : "neutral",
    headline: validation.loaded
      ? `${validation.failedResults} failed ${plural(
          validation.failedResults,
          "validation"
        )}`
      : "Validation data unavailable",
    supporting: validation.loaded
      ? `${validation.totalResults} results · ${validation.pendingRuns} pending ${plural(
          validation.pendingRuns,
          "run"
        )}`
      : "The validation dashboard did not return operational metrics.",
    metrics: [
      metric("Confidence", confidence),
      metric(
        "Evidence",
        `${Number(validation.evidenceCompletenessPct || 0).toFixed(0)}%`
      ),
      metric(
        "Freshness",
        `${Number(validation.evidenceFreshnessPct || 0).toFixed(0)}%`
      ),
      metric(
        "Failed controls",
        validation.failingControls || 0,
        validation.failingControls > 0 ? "danger" : "success"
      ),
    ],
    highlightTitle: validation.latestFailedValidation
      ? `${
          validation.latestFailedValidation.rule_id || "Validation"
        } validation failed`
      : validation.confidenceReason || "No validation issue highlighted",
    highlightDetail: validation.latestFailedValidation
      ? `${
          validation.latestFailedValidation.resource_id || "Resource"
        } · ${
          validation.latestFailedValidation.reason ||
          validation.confidenceReason ||
          "Review the failed validation."
        }`
      : `Compliance confidence: ${validation.confidenceLevel || "unknown"}`,
    highlightTone:
      validation.tone === "high"
        ? "danger"
        : validation.tone === "medium"
        ? "warning"
        : "success",
    actionLabel: "Open validation queue",
    explainAvailable: Boolean(validation.latestFailedValidation),
  };
}

function connectorSignal(connector = {}, formatConnectorProvider) {
  const topIssue = connector.topIssue;
  return {
    key: "connector",
    title: "Connector Health",
    description: "Collection readiness across governed integrations.",
    loaded: connector.loaded,
    statusLabel:
      connector.unhealthyCount > 0
        ? `${connector.unhealthyCount} ${plural(
            connector.unhealthyCount,
            "issue"
          )}`
        : "Healthy",
    statusTone: connector.unhealthyCount > 0 ? "high" : "low",
    headline: connector.loaded
      ? `${connector.total} monitored`
      : "Connector health unavailable",
    supporting: connector.loaded
      ? `${connector.healthy} healthy · ${connector.degraded} degraded · ${connector.failing} failing · ${connector.stale} stale`
      : "Connector health did not return operational metrics.",
    metrics: [
      metric("Healthy", connector.healthy || 0, "success"),
      metric(
        "Degraded",
        connector.degraded || 0,
        connector.degraded > 0 ? "warning" : "success"
      ),
      metric(
        "Failing",
        connector.failing || 0,
        connector.failing > 0 ? "danger" : "success"
      ),
      metric(
        "Stale",
        connector.stale || 0,
        connector.stale > 0 ? "warning" : "success"
      ),
    ],
    highlightTitle: topIssue
      ? `${formatConnectorProvider(topIssue.provider)} needs review`
      : "Connectors are collection-ready",
    highlightDetail: topIssue
      ? topIssue.activeDlq > 0
        ? `${topIssue.activeDlq} active DLQ ${plural(
            topIssue.activeDlq,
            "item"
          )}`
        : topIssue.recommendedAction ||
          `${topIssue.health || "unknown"} operational health`
      : "No degraded, failing, stale, or disconnected connector is currently highlighted.",
    highlightTone: topIssue ? "danger" : "success",
    actionLabel: "Open connector details",
    explainAvailable: Boolean(topIssue?.connector_id),
  };
}

// Provider-agnostic operational visibility. Connector health is not control assurance.
export function resolveConnectedSystemSignals(connectorState = {}, formatProvider = (value) => value) {
  if (!connectorState.loaded) return [];
  const rows = Array.isArray(connectorState.connectors) ? connectorState.connectors : [];
  const connected = rows.filter((item) =>
    ["connected", "active"].includes(String(item.connection_status || "").toLowerCase())
  );
  return connected.map((item, index) => {
    const provider = String(item.provider || "unknown");
    const health = String(item.operational_health || "unknown").toLowerCase();
    const hasCollection = Boolean(item.latest_success_at || item.last_collection_at || item.latest_sync_at);
    const observed = health === "healthy" && hasCollection;
    const bad = ["failing", "stale", "degraded"].includes(health);
    return {
      key: `connected-system-${provider}-${item.connector_id || index}`,
      title: `${formatProvider(provider)} · Collection`,
      description: "Tenant-owned connected system; operational collection health only.",
      loaded: true,
      statusLabel: bad ? health : observed ? "Collection healthy" : "Not assessed",
      statusTone: bad ? (health === "failing" ? "high" : "medium") : observed ? "low" : "neutral",
      headline: observed ? "Collection observed" : "Assessment not established",
      supporting: observed ? "Collection health does not establish control effectiveness." :
        "No verified control assessment is implied by this connector state.",
      metrics: [
        metric("Connection", "Connected"),
        metric("Collection", health === "unknown" ? "Unknown" : health),
        metric("Control assurance", "Not assessed"),
      ],
      highlightTitle: bad ? "Collection needs attention" : "Assurance requires validation",
      highlightDetail: bad ? (item.recommended_action || "Review connector collection health.") :
        "Review evidence and independently validate applicable controls.",
      highlightTone: bad ? "warning" : "neutral",
      actionLabel: "Open connector details",
    };
  });
}

export function workflowSignal(workflow = {}, formatDurationSeconds) {
  const hasWorkflows = Number(workflow.total || 0) > 0;
  return {
    key: "workflow",
    title: "Workflow Health",
    description: "Execution, dependency, approval, and validation health.",
    loaded: workflow.loaded,
    statusLabel: !workflow.loaded ? "Unavailable" : !hasWorkflows ? "No activity" : workflow.healthLabel,
    statusTone: workflow.loaded && hasWorkflows ? workflow.healthTone : "neutral",
    headline: workflow.loaded
      ? `${workflow.total} workflows`
      : "Workflow summary unavailable",
    supporting: workflow.loaded
      ? `${workflow.running} running · ${workflow.queued} queued · ${workflow.createdToday} created today`
      : "Workflow health did not return operational metrics.",
    metrics: [
      metric(
        "Queued",
        workflow.queued || 0,
        workflow.queued > 0 ? "warning" : "success"
      ),
      metric(
        "Blocked",
        workflow.blocked || 0,
        workflow.blocked > 0 ? "danger" : "success"
      ),
      metric(
        "Approval",
        workflow.awaitingApproval || 0,
        workflow.awaitingApproval > 0 ? "warning" : "success"
      ),
      metric(
        "Validation",
        workflow.awaitingValidation || 0,
        workflow.awaitingValidation > 0 ? "warning" : "success"
      ),
    ],
    highlightTitle:
      workflow.dependencyIssues > 0
        ? `${workflow.dependencyIssues} dependency ${plural(
            workflow.dependencyIssues,
            "item"
          )} need attention`
        : workflow.topBottleneck
        ? `Current bottleneck: ${String(
            workflow.topBottleneck.step_type || "workflow"
          ).replace(/_/g, " ")}`
        : !hasWorkflows ? "No workflow execution recorded" : "Dependencies clear",
    highlightDetail: `${workflow.completedToday || 0} completed today · avg duration ${formatDurationSeconds(
      workflow.averageDuration
    )}`,
    highlightTone:
      !hasWorkflows ? "neutral" : workflow.dependencyIssues > 0 || workflow.blocked > 0
        ? "danger"
        : "success",
    actionLabel: "Open workflow health",
  };
}

function SignalMetric({ label, value, tone }) {
  return (
    <div
      className={`operational-signal-metric operational-signal-metric--${tone}`}
    >
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SignalCard({ signal, onOpen, onExplain }) {
  return (
    <article
      className={`operational-signal-card operational-signal-card--${signal.highlightTone}`}
    >
      <header className="operational-signal-card__header">
        <div>
          <h3>{signal.title}</h3>
          <p>{signal.description}</p>
        </div>
        <MissionControlStatus tone={signal.statusTone}>
          {signal.statusLabel}
        </MissionControlStatus>
      </header>

      <div className="operational-signal-card__summary">
        <strong>{signal.headline}</strong>
        <span>{signal.supporting}</span>
      </div>

      <div className="operational-signal-card__metrics">
        {signal.metrics.map((item) => (
          <SignalMetric key={item.label} {...item} />
        ))}
      </div>

      <div
        className={`operational-signal-card__highlight operational-signal-card__highlight--${signal.highlightTone}`}
      >
        <div>
          <strong>{signal.highlightTitle}</strong>
          <span>{signal.highlightDetail}</span>
        </div>
      </div>

      <footer className="operational-signal-card__footer">
        {signal.explainAvailable && typeof onExplain === "function" ? (
          <Button variant="ghost" size="small" onClick={onExplain}>
            Explain
          </Button>
        ) : null}
        <Button variant="secondary" size="small" onClick={onOpen}>
          {signal.actionLabel}
        </Button>
      </footer>
    </article>
  );
}

export default function OperationalSignals({
  validation,
  connector,
  workflow,
  formatConnectorProvider,
  formatDurationSeconds,
  onOpenValidation,
  onExplainValidation,
  onOpenConnectors,
  onExplainConnector,
  onOpenWorkflow,
}) {
  const signals = [
    {
      signal: validationSignal(validation),
      onOpen: onOpenValidation,
      onExplain: onExplainValidation,
    },
    {
      signal: connectorSignal(connector, formatConnectorProvider),
      onOpen: onOpenConnectors,
      onExplain: onExplainConnector,
    },
    ...resolveConnectedSystemSignals(connector, formatConnectorProvider).map((signal) => ({
      signal,
      onOpen: onOpenConnectors,
    })),
    {
      signal: workflowSignal(workflow, formatDurationSeconds),
      onOpen: onOpenWorkflow,
    },
  ];

  return (
    <MissionControlSection
      eyebrow="Operational signals"
      title="What needs attention now"
      description="Operational signals for validation, connected systems, and governed workflow execution. Collection health is not control assurance."
      className="operational-signals"
    >
      <div className="operational-signals__grid">
        {signals.map(({ signal, onOpen, onExplain }) => (
          <SignalCard
            key={signal.key}
            signal={signal}
            onOpen={onOpen}
            onExplain={onExplain}
          />
        ))}
      </div>
    </MissionControlSection>
  );
}
